import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { requireFeature } from "@/lib/feature-gate";

export const Route = createFileRoute("/user/_layout/inventory/requisitions")({
  beforeLoad: () => requireFeature("inventory"),
  component: UserRequisitionsPage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchRequisitions,
  useRequisitionMutations,
  type RequisitionOrdering,
} from "@/hooks/api/inventory/useStockOps";
import {
  ALL_ACTIVE_PARAMS,
  useFetchWarehouses,
  useFetchItems,
  useFetchUnits,
} from "@/hooks/api/inventory/useCatalog";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDebounce } from "@/hooks/use-debounce";
import { RequisitionTable } from "@/components/inventory/tables/RequisitionTable";
import { RequisitionFormSheet } from "@/components/inventory/forms/RequisitionFormSheet";
import { RequisitionDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import { useAuthStore } from "@/stores/useAuthStore";
import type {
  Requisition,
  RequisitionPayload,
  RequisitionStatus,
} from "@/types/inventory";

const STATUS_OPTIONS = [
  { label: "Requested", value: "REQUESTED" },
  { label: "Approved", value: "APPROVED" },
  { label: "Partially Issued", value: "PARTIALLY_ISSUED" },
  { label: "Issued", value: "ISSUED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Cancelled", value: "CANCELLED" },
] satisfies { label: string; value: RequisitionStatus }[];

function UserRequisitionsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    RequisitionStatus | undefined
  >(undefined);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [viewing, setViewing] = useState<Requisition | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Requisition | null>(null);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        requisition_number: SortDirection;
        status: SortDirection;
        requisition_date: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      RequisitionOrdering
    >({
      requisition_number: null,
      status: null,
      requisition_date: null,
      created_at: null,
      updated_at: null,
    });

  const requisitionsQuery = useFetchRequisitions(
    {
      page,
      page_size: pageSize,
      requested_by: user?.user_id,
      search: debouncedSearch || undefined,
      ordering: getApiOrdering(),
      // axios drops undefined params, so no selection means no filter.
      status: statusFilter,
      warehouse: warehouseFilter ? Number(warehouseFilter) : undefined,
    },
    { enabled: user?.user_id != null }
  );
  const requisitions = requisitionsQuery.data?.results ?? [];
  const count = requisitionsQuery.data?.count ?? 0;

  const { createRequisition, cancelRequisition } = useRequisitionMutations();
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const itemsQuery = useFetchItems(ALL_ACTIVE_PARAMS);
  const items = itemsQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS);
  const units = unitsQuery.data?.results ?? [];

  const isMutating = createRequisition.isPending || cancelRequisition.isPending;
  const isLoading = requisitionsQuery.isPending || isMutating;
  const isFetching = requisitionsQuery.isFetching || isMutating;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, statusFilter, warehouseFilter, setPage]);

  const resetFilters = () => {
    setOrdering({
      requisition_number: null,
      status: null,
      requisition_date: null,
      created_at: null,
      updated_at: null,
    });
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setWarehouseFilter(undefined);
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
  };

  const handleSubmit = async (data: RequisitionPayload) => {
    await createRequisition.mutateAsync(data);
    toast.success("Requisition created");
    setIsSheetOpen(false);
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await cancelRequisition.mutateAsync({ id: cancelTarget.id });
      toast.success("Requisition cancelled");
      setCancelTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="My Requisitions"
          description="Request stock withdrawals from warehouses"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => setIsSheetOpen(true)}
          addLabel="New Requisition"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search REQ number..."
            value={searchInput}
            onChange={setSearchInput}
          />
          <SelectFilter
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
          <SelectFilter
            placeholder="Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={warehouseFilter}
            onChange={(v) => setWarehouseFilter(v)}
          />
        </FiltersPanel>

        <RequisitionTable
          requisitions={requisitions}
          isLoading={isLoading}
          userMode
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onNameClick={(r) =>
            navigate({
              to: "/user/inventory/requisitions/$id",
              params: { id: String(r.id) },
            })
          }
          onView={(r) => setViewing(r)}
          onApprove={() => {}}
          onReject={() => {}}
          onIssue={() => {}}
          onCancel={(r) => setCancelTarget(r)}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <RequisitionDetailSheet
          isOpen={!!viewing}
          onClose={() => setViewing(null)}
          req={viewing}
        />

        <RequisitionFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          warehouses={warehouses}
          items={items}
          units={units}
          isLoading={isMutating}
        />

        <DeleteConfirmDialog
          isOpen={!!cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleCancelConfirm}
          title="Cancel Requisition"
          description={`Cancel requisition ${cancelTarget?.requisition_number}? This cannot be undone.`}
          itemName={cancelTarget?.requisition_number ?? ""}
          isLoading={isMutating}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
